/**
 * Helpers purs prévention côté frontend.
 *
 * Ces fonctions ne font AUCUN appel réseau ; elles opèrent sur les données
 * déjà chargées (batiments, grilles) et sont testées unitairement.
 */

/**
 * Retourne la LISTE des grilles applicables à un bâtiment donné, à partir de
 * ses attributs (`groupe_occupation` / `sous_type_batiment` / `niveau_risque`).
 *
 * Contrairement à `getDefaultGrille` (Prevention.jsx) qui renvoie « la
 * meilleure » avec un fallback générique, `getApplicableGrilles` renvoie
 * UNIQUEMENT les grilles matchant explicitement au moins un attribut. Peut
 * retourner un tableau vide → à traiter comme « aucune grille configurée
 * pour ce bâtiment ».
 *
 * Utilisée par le flux Inspecter (pompier) pour brancher 0 / 1 / N :
 *   - 0 → bloquer avec message clair
 *   - 1 → skip la SETUP, ouvrir directement le formulaire d'inspection
 *   - N → ouvrir la SETUP simplifiée avec choix parmi ces N
 *
 * @param {Object|null|undefined} batiment - bâtiment avec `groupe_occupation`,
 *   `sous_type_batiment`, `niveau_risque` (tous optionnels).
 * @param {Array<Object>|null|undefined} grilles - liste des grilles actives
 *   du tenant.
 * @returns {Array<Object>} sous-ensemble dédoublonné (par id) des grilles
 *   matchant au moins un attribut du bâtiment.
 */
export function getApplicableGrilles(batiment, grilles) {
  if (!batiment || !Array.isArray(grilles) || grilles.length === 0) return [];
  const idsMatchants = new Set();

  const { groupe_occupation, sous_type_batiment, niveau_risque } = batiment;

  // Match par sous_type_batiment (ex. "F-2")
  if (sous_type_batiment) {
    for (const g of grilles) {
      if (
        g.groupe_occupation === sous_type_batiment ||
        (g.nom || '').toLowerCase().includes(String(sous_type_batiment).toLowerCase())
      ) {
        idsMatchants.add(g.id);
      }
    }
  }

  // Match par groupe_occupation principal (ex. "F")
  if (groupe_occupation) {
    const tag = `groupe ${String(groupe_occupation).toLowerCase()}`;
    for (const g of grilles) {
      if (
        g.groupe_occupation === groupe_occupation ||
        (g.nom || '').toLowerCase().includes(tag)
      ) {
        idsMatchants.add(g.id);
      }
    }
  }

  // Match par niveau_risque
  if (niveau_risque) {
    for (const g of grilles) {
      if (g.niveau_risque === niveau_risque) idsMatchants.add(g.id);
    }
  }

  // Retour dans l'ordre d'apparition dans `grilles` (stable) et dédoublonné.
  return grilles.filter(g => idsMatchants.has(g.id));
}
